import { format } from "date-fns";

import type { QuoteBoardItem } from "~/shared/schemas/quote-board-schema.ts";

import { resolveQuoteBoardStatus } from "./quote-board-status.ts";

/**
 * Helper function to format date strings safely
 */
export const formatDateString = (
	dateValue: string | undefined | null,
): string | undefined | null => {
	if (!dateValue) return dateValue;
	try {
		return format(new Date(dateValue), "MM/dd/yyyy");
	} catch {
		return dateValue; // fallback to original value if parsing fails
	}
};

/**
 * Helper function to format currency values safely with 2 decimal places
 */
export const formatCurrency = (
	value: string | number | null | undefined,
): string => {
	if (value == null) return "$0.00";

	const numValue = typeof value === "string" ? Number.parseFloat(value) : value;
	if (Number.isNaN(numValue)) return "$0.00";

	return `$${numValue.toFixed(2)}`;
};

/**
 * Determines if a quote board item has children based on business logic
 */
export const hasChildrenData = (item: QuoteBoardItem): boolean => {
	return (
		item.bulkQuoteId != null ||
		(item.requestCount != null && item.requestCount > 1)
	);
};

/**
 * Transform quote board data for grid display
 */
export const transformQuoteBoardData = (
	gridData: QuoteBoardItem[],
	isInternal = false,
): (Omit<
	QuoteBoardItem,
	| "customerRate"
	| "carrierRate"
	| "totalWeight"
	| "margin"
	| "poNumber"
	| "maxLiability"
> & {
	hasChildren: boolean;
	statusDisplay: string | undefined;
	customerRate: number | null;
	carrierRate: number | null;
	totalWeight: number;
	margin: number | null;
	poNumber: number | null;
	maxLiability: string;
})[] => {
	if (!Array.isArray(gridData)) {
		return [];
	}

	return gridData
		.filter((item): item is QuoteBoardItem => {
			return item != null && typeof item === "object";
		})
		.map((item) => {
			const { label } = resolveQuoteBoardStatus(item, { isInternal });
			const customerRateNum =
				item.customerRate == null
					? null
					: Number.parseFloat(String(item.customerRate)) || 0;
			const carrierRateNum =
				item.carrierRate == null
					? null
					: Number.parseFloat(String(item.carrierRate)) || 0;
			return {
				...item,
				// Clear carrier name when no carrier has been selected yet
				carrierName:
					item.statusLevel === "Carrier Selection" ? null : item.carrierName,
				// Keep numeric values for sorting
				totalWeight:
					typeof item.totalWeight === "string"
						? Number.parseFloat(item.totalWeight) || 0
						: (item.totalWeight ?? 0),
				customerRate: customerRateNum,
				carrierRate: carrierRateNum,
				margin: item.margin,
				maxLiability: item.maxLiability,
				poNumber:
					item.poNumber == null
						? null
						: Number.parseFloat(String(item.poNumber)) || 0,
				// Keep original date values for sorting
				shipmentDate: item.shipmentDate,
				createdDate: item.createdDate,
				hasChildren: false,
				statusDisplay: label,
			};
		});
};
